// ADR 15.9.26aj §5 — a text link, decided once.
//
// Twelve `<Link className="text-sm underline">`s shipped with the user agent's
// focus outline while every button, field and row drew the design's. This is
// the `ghost` button's look on an anchor, with `FOCUS_RING` — and
// `styleFloor.test.ts` AC6 is what keeps the next bare `<a>` from shipping
// without one. A link that wraps a `Button` or a `Card` (`emptyStates.tsx`,
// `ImageSlot.tsx`) is not a text link and takes the ring on the `<Link>`
// directly: an `Anchor` around a `Button` would draw a ring inside a ring.
//
// `className` is *appended* after the variant's classes, never in place of
// them, so a `block` link keeps its `block` and its ring.

import type { MouseEventHandler, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router-dom'

import { FOCUS_RING } from './Button'

export type AnchorVariant = 'inline' | 'quiet'

const VARIANT: Record<AnchorVariant, string> = {
  inline: `rounded text-body-md underline ${FOCUS_RING}`,
  quiet: `rounded text-body-md text-ink-muted ${FOCUS_RING}`,
}

/**
 * ADR 24.9.26l §5 — WCAG 2.5.8's 24px floor, on every standalone link. A link
 * inside running prose is the criterion's inline exception and passes
 * `inProse`: the floor would stop it wrapping with its sentence, a 1.4.10
 * hazard at 320px. (Not `inline` — that is already a variant, "underlined".)
 */
const TARGET_FLOOR = 'inline-flex min-h-6 items-center'

/**
 * The class string an `Anchor` composes, exported for 24.9.26l AC12. A caller's
 * `block` keeps its display: Tailwind emits `inline-flex` after `block`, so the
 * floor's own display would win, and a block box honours `min-h-6` by itself.
 */
export function anchorClassName(variant: AnchorVariant, inProse: boolean, className = ''): string {
  const floor = inProse ? '' : /(^|\s)block(\s|$)/.test(className) ? 'min-h-6' : TARGET_FLOOR
  return [floor, VARIANT[variant], className].filter((part) => part !== '').join(' ')
}

/** A `<Link>` for `to`, an `<a>` for `href` — a discriminated union, so the branch is the one the types name. */
export type AnchorProps = {
  variant?: AnchorVariant
  /** Inside running prose: no 24px floor, so the link wraps with the text. */
  inProse?: boolean
  className?: string
  onClick?: MouseEventHandler<HTMLAnchorElement>
  'data-testid'?: string
  children: ReactNode
} & (
  | { to: LinkProps['to']; href?: never; replace?: boolean }
  // ADR 30.9.26i §2.1 — an off-site page opened beside the app (`_blank` + `noopener`).
  | { href: string; to?: never; target?: '_blank'; rel?: string }
)

export function Anchor(props: AnchorProps) {
  const { variant = 'inline', inProse = false, className = '', onClick, children } = props
  const classes = anchorClassName(variant, inProse, className)
  const testId = props['data-testid']
  if (props.to === undefined) {
    return (
      <a
        className={classes}
        href={props.href}
        target={props.target}
        rel={props.rel}
        onClick={onClick}
        data-testid={testId}
      >
        {children}
      </a>
    )
  }
  return (
    <Link className={classes} to={props.to} replace={props.replace} onClick={onClick} data-testid={testId}>
      {children}
    </Link>
  )
}
